/**
 * Module-pane persistence/hydration + module-catalog helpers — extracted from
 * ChatBox.tsx (PR #3277 decomposition).
 *
 * Each helper is a plain async function that takes an explicit deps object so
 * the in-component useCallback shells stay byte-for-byte (same dep arrays).
 * Bodies are reproduced verbatim from ChatBox; the inflight race-guard
 * semantics (panelCreateInflightRef, moduleAttachInFlightRef) are preserved
 * exactly. The catalog helpers (refreshWorkspaceModuleCatalog,
 * loadConversationPanels, refreshPanelBoundModules) load the workspace module
 * catalog, conversation panels, and panel-bound modules respectively.
 */
import type { MutableRefObject } from 'react';
import { api } from '@/lib/api';
import type { ConversationPanel, WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { Module } from '@/types/modules';
import { isRecord } from '@/features/chat/utils/chat-text';
import {
  parseChatPanePanelState,
  getChatPaneStateTimestamp,
} from '@/features/chat/utils/chat-pane-state';
import { toChatModuleFromWorkspaceModule } from '@/features/chat/utils/workspace-module-mapping';

type LoadConversationPanels = (
  conversationId: string,
  options?: { forceRefresh?: boolean },
) => Promise<ConversationPanel[]>;

type UpsertConversationPanelCache = (
  conversationId: string,
  panel: ConversationPanel,
) => void;

export interface PersistModulePaneStateDeps {
  token: string | null;
  workspaceId: string | null;
  loadConversationPanels: LoadConversationPanels;
  upsertConversationPanelCache: UpsertConversationPanelCache;
  panelCreateInflightRef: MutableRefObject<Set<string>>;
  conversationPanelsCacheRef: MutableRefObject<Record<string, ConversationPanel[]>>;
}

export async function persistModulePaneState(
  conversationId: string,
  moduleId: string,
  paneState: { isVisible: boolean; isFullscreen: boolean; anchorMessageId?: string | null },
  deps: PersistModulePaneStateDeps,
): Promise<void> {
  const {
    token,
    workspaceId,
    loadConversationPanels,
    upsertConversationPanelCache,
    panelCreateInflightRef,
    conversationPanelsCacheRef,
  } = deps;
  if (!token || !workspaceId || !conversationId || !moduleId) return;

  try {
    let panels = await loadConversationPanels(conversationId);
    let panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;

    if (!panel) {
      const inflightKey = `${conversationId}:${moduleId}`;
      if (panelCreateInflightRef.current.has(inflightKey)) {
        // Another call is already creating this panel — wait and re-fetch
        await new Promise<void>((r) => setTimeout(r, 500));
        panels = await loadConversationPanels(conversationId, { forceRefresh: true });
        panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;
        if (!panel) return;
      } else {
        panelCreateInflightRef.current.add(inflightKey);
        try {
          const createdPanel = await api.workspaces.createConversationPanel(
            token,
            workspaceId,
            conversationId,
            {
              workspaceModuleId: moduleId,
              panelKey: `module:${moduleId}`,
            },
          );
          upsertConversationPanelCache(conversationId, createdPanel);
          panels = conversationPanelsCacheRef.current[conversationId] || panels;
          panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;
        } catch (createError: any) {
          // 409 means the panel already exists — just re-fetch and continue
          if (createError?.status !== 409) {
            console.warn('[ChatBox] Failed to create panel for module pane state persistence', {
              conversationId,
              moduleId,
              createError,
            });
          }
          panels = await loadConversationPanels(conversationId, { forceRefresh: true });
          panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;
        } finally {
          panelCreateInflightRef.current.delete(inflightKey);
        }
      }
    }

    if (!panel) return;

    const currentPanelState = isRecord(panel.state) ? panel.state : {};
    const currentChatPaneState = parseChatPanePanelState(currentPanelState.chatPane);
    const nextAnchorMessageId = paneState.anchorMessageId !== undefined
      ? paneState.anchorMessageId
      : (currentChatPaneState?.anchorMessageId ?? null);
    const nextState = {
      ...currentPanelState,
      chatPane: {
        open: paneState.isVisible,
        fullscreen: paneState.isFullscreen,
        anchorMessageId: nextAnchorMessageId,
        updatedAt: new Date().toISOString(),
      },
    };
    const updatedPanel = await api.workspaces.updateConversationPanel(
      token,
      workspaceId,
      conversationId,
      panel.id,
      { state: nextState },
    );
    upsertConversationPanelCache(conversationId, updatedPanel);
  } catch (error) {
    console.warn('[ChatBox] Failed to persist module pane state', {
      conversationId,
      moduleId,
      error,
    });
  }
}

export interface HydrateModulePaneStateDeps {
  token: string | null;
  workspaceId: string | null;
  loadConversationPanels: LoadConversationPanels;
}

export async function hydrateModulePaneState(
  conversationId: string,
  deps: HydrateModulePaneStateDeps,
): Promise<{
  moduleId: string;
  isVisible: boolean;
  isFullscreen: boolean;
  anchorMessageId: string | null;
} | null> {
  const { token, workspaceId, loadConversationPanels } = deps;
  if (!token || !workspaceId || !conversationId) return null;

  try {
    const panels = await loadConversationPanels(conversationId, { forceRefresh: true });
    const candidates = panels
      .filter((panel) => !panel.archivedAt)
      .map((panel) => ({
        panel,
        chatPane: parseChatPanePanelState(isRecord(panel.state) ? panel.state.chatPane : undefined),
      }))
      .filter((entry) => Boolean(entry.chatPane));

    if (candidates.length === 0) return null;

    candidates.sort((left, right) => (
      getChatPaneStateTimestamp(right.chatPane, right.panel.updatedAt)
      - getChatPaneStateTimestamp(left.chatPane, left.panel.updatedAt)
    ));

    const latest = candidates[0];
    if (!latest?.panel?.moduleId || !latest.chatPane) return null;

    return {
      moduleId: latest.panel.moduleId,
      isVisible: latest.chatPane.open,
      isFullscreen: latest.chatPane.fullscreen,
      anchorMessageId: latest.chatPane.anchorMessageId ?? null,
    };
  } catch (error) {
    console.warn('[ChatBox] Failed to hydrate module pane state', { conversationId, error });
    return null;
  }
}

export interface EnsureModulePanelInConversationDeps {
  token: string | null;
  workspaceId: string | null;
  loadConversationPanels: LoadConversationPanels;
  upsertConversationPanelCache: UpsertConversationPanelCache;
}

export async function ensureModulePanelInConversation(
  conversationId: string,
  moduleId: string,
  deps: EnsureModulePanelInConversationDeps,
): Promise<boolean> {
  const { token, workspaceId, loadConversationPanels, upsertConversationPanelCache } = deps;
  if (!token || !workspaceId) return false;

  try {
    const panels = await loadConversationPanels(conversationId, { forceRefresh: true });
    const alreadyAttached = panels.some((panel) => !panel.archivedAt && panel.moduleId === moduleId);
    if (alreadyAttached) return true;

    const workspaceModules = await api.workspaces.listWorkspaceModules(token, workspaceId, {
      includeArchived: true,
    });
    const moduleExists = workspaceModules.some((module) => module.id === moduleId && !module.archivedAt);
    if (!moduleExists) return false;

    const createdPanel = await api.workspaces.createConversationPanel(
      token,
      workspaceId,
      conversationId,
      {
        workspaceModuleId: moduleId,
        panelKey: `module:${moduleId}`,
      },
    );
    upsertConversationPanelCache(conversationId, createdPanel);
    return true;
  } catch (error) {
    console.warn('[ChatBox] Failed to attach module panel to conversation', {
      conversationId,
      moduleId,
      error,
    });
    return false;
  }
}

export interface EnsureModulePanelInConversationOnceDeps {
  moduleAttachInFlightRef: MutableRefObject<Map<string, Promise<boolean>>>;
  ensureModulePanelInConversation: (
    conversationId: string,
    moduleId: string,
  ) => Promise<boolean>;
}

export async function ensureModulePanelInConversationOnce(
  conversationId: string,
  moduleId: string,
  deps: EnsureModulePanelInConversationOnceDeps,
): Promise<boolean> {
  const { moduleAttachInFlightRef, ensureModulePanelInConversation } = deps;
  const key = `${conversationId}:${moduleId}`;
  const existing = moduleAttachInFlightRef.current.get(key);
  if (existing) return existing;

  const pending = ensureModulePanelInConversation(conversationId, moduleId)
    .finally(() => {
      moduleAttachInFlightRef.current.delete(key);
    });

  moduleAttachInFlightRef.current.set(key, pending);
  return pending;
}

export interface RefreshWorkspaceModuleCatalogDeps {
  token: string | null;
  workspaceId: string | null;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  setWorkspaceModuleLabels: (labels: string[]) => void;
}

export async function refreshWorkspaceModuleCatalog(
  deps: RefreshWorkspaceModuleCatalogDeps,
): Promise<WorkspaceModule[]> {
  const { token, workspaceId, workspaceModuleCatalogRef, setWorkspaceModuleLabels } = deps;
  if (!token || !workspaceId) {
    workspaceModuleCatalogRef.current = [];
    setWorkspaceModuleLabels([]);
    return [];
  }

  try {
    const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {
      includeArchived: false,
    });
    workspaceModuleCatalogRef.current = modules;
    const labels = Array.from(
      new Set(
        modules
          .map((module) => (typeof module.label === 'string' ? module.label.trim() : ''))
          .filter((label) => label.length > 0),
      ),
    );
    setWorkspaceModuleLabels(labels);
    return modules;
  } catch (error) {
    console.warn('[ChatBox] Failed to refresh workspace module catalog', error);
    return workspaceModuleCatalogRef.current;
  }
}

export interface LoadConversationPanelsDeps {
  token: string | null;
  workspaceId: string | null;
  conversationPanelsCacheRef: MutableRefObject<Record<string, ConversationPanel[]>>;
}

export async function loadConversationPanels(
  conversationId: string,
  options: { forceRefresh?: boolean } | undefined,
  deps: LoadConversationPanelsDeps,
): Promise<ConversationPanel[]> {
  const { token, workspaceId, conversationPanelsCacheRef } = deps;
  if (!token || !workspaceId || conversationId.startsWith('conversation-')) return [];

  const cachedPanels = conversationPanelsCacheRef.current[conversationId];
  if (cachedPanels && !options?.forceRefresh) return cachedPanels;

  const panels = await api.workspaces.listConversationPanels(
    token,
    workspaceId,
    conversationId,
    { includeArchived: true },
  );
  conversationPanelsCacheRef.current[conversationId] = panels;
  return panels;
}

export interface RefreshPanelBoundModulesDeps {
  loadConversationPanels: LoadConversationPanels;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  activeConversationIdRef: MutableRefObject<string | null>;
  setPanelBoundModules: (modules: Module[]) => void;
}

export async function refreshPanelBoundModules(
  conversationId: string,
  options: { forceRefresh?: boolean } | undefined,
  deps: RefreshPanelBoundModulesDeps,
): Promise<Module[]> {
  const {
    loadConversationPanels,
    refreshWorkspaceModuleCatalog,
    workspaceModuleCatalogRef,
    activeConversationIdRef,
    setPanelBoundModules,
  } = deps;
  if (!conversationId) return [];

  try {
    const panels = await loadConversationPanels(conversationId, {
      forceRefresh: options?.forceRefresh,
    });
    const panelModuleIds = Array.from(
      new Set(
        panels
          .filter((panel) => !panel.archivedAt && typeof panel.moduleId === 'string' && panel.moduleId.trim())
          .map((panel) => panel.moduleId),
      ),
    );
    if (panelModuleIds.length === 0) {
      if (activeConversationIdRef.current === conversationId) {
        setPanelBoundModules([]);
      }
      return [];
    }

    let workspaceModules = workspaceModuleCatalogRef.current;
    if (options?.forceRefresh || workspaceModules.length === 0) {
      workspaceModules = await refreshWorkspaceModuleCatalog();
    }
    const workspaceModuleById = new Map(
      workspaceModules
        .filter((module) => !module.archivedAt)
        .map((module) => [module.id, module]),
    );
    const resolvedModules = panelModuleIds
      .map((moduleId, index) => {
        const workspaceModule = workspaceModuleById.get(moduleId);
        if (!workspaceModule) return null;
        return toChatModuleFromWorkspaceModule(workspaceModule, conversationId, index + 1);
      })
      .filter((module): module is Module => Boolean(module));

    if (activeConversationIdRef.current === conversationId) {
      setPanelBoundModules(resolvedModules);
    }
    return resolvedModules;
  } catch (error) {
    console.warn('[ChatBox] Failed to refresh panel-bound modules', { conversationId, error });
    return [];
  }
}
